import { useReducer, useEffect, useCallback } from 'react'
import { Box, DialogContent } from '@mui/material'
import MUIDialog from '@mui/material/Dialog'
import { t } from 'i18next'

import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import {
  CheckFillIcon,
  CloseRoundFullIcon,
  NumberOneIcon,
  NumberTwoIcon,
  NumberThreeIcon,
  NumberFourIcon,
  NumberFiveIcon
} from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { SubmitStagesStyles as styles } from 'src/components/shared/ui/SubmitStagesDialog/SubmitStagesStyles'
import {
  SubmitStageProp,
  SubmitStageStatus,
  SubmitStage,
  SubmitStageStep
} from 'src/components/shared/ui/SubmitStagesDialog/SubmitStagesTypes'
import { theme } from 'src/providers/ThemeProvider'
import DialogTitle from '../DialogTitle/DialogTitle'

export const initialStages: SubmitStage[] = [
  {
    step: SubmitStageStep.STEP_1,
    status: SubmitStageStatus.INITIAL,
    message: 'Conformando datos del escrito',
    error: null,
    icon: <NumberOneIcon />
  },
  {
    step: SubmitStageStep.STEP_2,
    status: SubmitStageStatus.INITIAL,
    message: 'Enviando escrito',
    error: null,
    icon: <NumberTwoIcon />
  },
  {
    step: SubmitStageStep.STEP_3,
    status: SubmitStageStatus.INITIAL,
    message: 'Pidiendo Firma Digital',
    error: null,
    icon: <NumberThreeIcon />
  },
  {
    step: SubmitStageStep.STEP_4,
    status: SubmitStageStatus.INITIAL,
    message: 'Firmando archivos adjuntos',
    error: null,
    icon: <NumberFourIcon />
  },
  {
    step: SubmitStageStep.STEP_5,
    status: SubmitStageStatus.INITIAL,
    message: 'Enviando escrito firmado',
    error: null,
    icon: <NumberFiveIcon />
  }
]

type Props = {
  show: boolean
  selectedStage: SubmitStageProp
  close: () => void
  disabledButton: boolean
}

export default function SubmitStagesDialog({
  show,
  selectedStage,
  close,
  disabledButton
}: Props): JSX.Element {
  const reducer = (stages: SubmitStage[], selectedStage: SubmitStageProp) => {
    return stages.map((stage, index) => {
      stage.error = null
      if (selectedStage.step === index + 1) {
        const { status, error } = selectedStage
        stage.status = status
        if (selectedStage.status === SubmitStageStatus.ERROR) {
          stage.error = error
        }
      }
      return stage
    })
  }

  const [stages, dispatchStages] = useReducer(reducer, initialStages)

  const renderStatusIcon = useCallback((status: SubmitStageStatus | null): JSX.Element | null => {
    switch (status) {
      case SubmitStageStatus.PROCESSING:
        return (
          <Box sx={styles.loader}>
            <Loader size="small" />
          </Box>
        )
      case SubmitStageStatus.SUCCESS:
        return <CheckFillIcon htmlColor={theme.colors.seaGreen100} />
      case SubmitStageStatus.ERROR:
        return <CloseRoundFullIcon htmlColor={theme.colors.persianRed100} />
      default:
        return null
    }
  }, [])

  function getClassName(status: SubmitStageStatus | null): string | undefined {
    switch (status) {
      case SubmitStageStatus.INITIAL:
        return 'initial'
      case SubmitStageStatus.PROCESSING:
        return 'processing'
      case SubmitStageStatus.SUCCESS:
        return 'success'
      case SubmitStageStatus.ERROR:
        return 'error'
      default:
        return undefined
    }
  }

  function getInfoText(stage: SubmitStage): string {
    switch (stage.status) {
      case SubmitStageStatus.PROCESSING:
        return 'Ejecutando'
      case SubmitStageStatus.SUCCESS:
        return 'Completado'
      case SubmitStageStatus.ERROR:
        return 'Error'
      default:
        return ''
    }
  }

  useEffect(() => {
    if (selectedStage.step) {
      dispatchStages(selectedStage)
    }
  }, [selectedStage])

  return (
    <MUIDialog open={show} fullWidth maxWidth="sm" fullScreen={false} sx={styles.container}>
      <DialogTitle id="send-dialog">{t('dialog.title.conectar_lexnet')}</DialogTitle>
      <DialogContent sx={styles.content}>
        <Box component="ul">
          {stages.map(stage => (
            <Box
              component="li"
              key={stage.message}
              className={getClassName(stage.status)}
              sx={styles.stageListItem}
            >
              <Box sx={styles.row1}>
                <Box sx={styles.statusText}>{stage.message}</Box>
                <Box sx={styles.statusMessage} className={getClassName(stage.status)}>
                  {getInfoText(stage)}
                </Box>
                <Box sx={styles.statusIcon} className={getClassName(stage.status)}>
                  {renderStatusIcon(stage.status)}
                </Box>
              </Box>
              <Box sx={styles.rowProgress}>
                <Box sx={styles.progress} />
              </Box>
              {stage.error && (
                <Box component="p" sx={styles.errorInfo}>
                  {stage.error}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <Box sx={styles.sendButton}>
        <ButtonPrimaryOutlined disabled={disabledButton} onClick={close}>
          {t('Aceptar')}
        </ButtonPrimaryOutlined>
      </Box>
    </MUIDialog>
  )
}
